import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Box } from 'shared/components/Box/Box'

import { FlexBox } from '../../../components/Base/FlexBox'
import { Wrap } from '../../../components/Base/Wrap'
import { Button } from '../../../components/Button/Button'
import { useUserProvider } from '../../../components/Context/UserContext'
import { SimpleDialog } from '../../../components/Dialog/SimpleDialog'
import { SSNInput } from '../../../components/MaskedInput/SSNInput'
import { InlineSpinner } from '../../../components/Spinner'
import { Text } from '../../../components/Text/Text'
import { Handler } from '../../../helpers/typeHelper'
import { useUserV3 } from '../../../hooks/query/useUserV3'
import { revealSsn, updateSsn, User } from '../../../models/user'
import { bgGray } from '../../../theme/colors'
import { ProfileCardItem } from './ProfileCard'

interface ProfileSSNProps {
  user: User
  canEdit: boolean
}

export function ProfileSSN({ user, canEdit }: ProfileSSNProps) {
  const [showModal, setShowModal] = useState(false)

  return (
    <ProfileCardItem label="SSN">
      <FlexBox alignItems="center" gap="10px">
        <span>••• •• •••• </span>
        {canEdit && (
          <Button size="small" variant="secondary" onClick={() => setShowModal(true)}>
            Reveal
          </Button>
        )}
      </FlexBox>
      {showModal && canEdit && <ProfileSSNModal user={user} onClose={() => setShowModal(false)} />}
    </ProfileCardItem>
  )
}

interface ProfileSSNModalProps {
  user: User
  onClose: Handler
}

export function ProfileSSNModal({ user, onClose }: ProfileSSNModalProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)
  const [ssn, setSSN] = useState('')

  useEffect(() => {
    revealSsn(user.id).then((data) => {
      setIsLoading(false)
      setSSN(data.ssnEncrypted)
    })
  }, [user.id])

  function renderBody() {
    if (isLoading) return <InlineSpinner mt={5} mb={5} />
    if (isEditing) return <ProfileSSNEdit user={user} onClose={onClose} />

    return (
      <>
        <Text variant="header2">{ssn}</Text>
        <Button size="medium" onClick={() => setIsEditing(true)} margin="10px auto 0">
          Edit
        </Button>
      </>
    )
  }

  return (
    <SimpleDialog isOpen onClose={onClose}>
      <Wrap width="300px" maxWidth="100%">
        {renderBody()}
      </Wrap>
    </SimpleDialog>
  )
}

interface ProfileSSNModalProps {
  user: User
  onClose: Handler
}

export function ProfileSSNEdit({ user, onClose }: ProfileSSNModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const { refetch } = useUserV3(user.id, { enabled: false })
  const { showBanner, clearUserCache } = useUserProvider()

  const { handleSubmit, control } = useForm<{ ssn: string }>({
    defaultValues: {
      ssn: '',
    },
    mode: 'onSubmit',
  })

  async function onSubmit({ ssn }: { ssn: string }) {
    setIsLoading(true)
    try {
      await updateSsn(user.id, { ssn: ssn.replaceAll(' ', '') })
      await Promise.all([clearUserCache(), refetch()])
      showBanner('success', { message: 'SSN changed!' })
      onClose()
    } catch (err: any) {
      showBanner('error', err)
    }
    setIsLoading(false)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        render={({ field, fieldState }) => (
          <SSNInput {...field} label="SSN" error={fieldState.error?.message} backgroundColor={bgGray} />
        )}
        name="ssn"
        control={control}
      />

      <Box mt={7} />
      <Button variant="primary" type="submit" size="extraLarge" margin="0 auto" isLoading={isLoading}>
        Confirm
      </Button>
      <Box mt={3} />
      <Button variant="text" onClick={onClose} margin="0 auto">
        Cancel
      </Button>
    </form>
  )
}
